import { injectable, decorate, inject } from 'inversify';
import 'reflect-metadata';
import { map, get } from 'lodash';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';
import { sapFullDateFormat } from 'constants/dateFormats';
import {
  convertDateToISOFormat,
  convertDateFormat,
  formatDate,
} from 'utils/date';
import { downloadFile, extractFilenameFromHeader } from 'utils/file';

@injectable()
class CascadeService {
  constructor(apiService) {
    this.apiService = apiService;
  }

  async getCascadeList(params, abortSignal) {
    const result = await this.apiService.get('/cascade/list', params, {
      signal: abortSignal,
    });

    return result.data.data.items;
  }

  async getPayments(entityId, abortSignal) {
    const result = await this.apiService.get(
      `/cascade/${entityId}/payments`,
      undefined,
      abortSignal,
    );

    return result.data.data.items;
  }

  async getFiles(entityId, abortSignal) {
    const result = await this.apiService.get(
      `/cascade/${entityId}/files`,
      undefined,
      abortSignal,
    );

    return result.data.data.items;
  }

  async uploadFiles(entityId, fileTypeId, files) {
    const formData = new FormData();
    formData.append('file_type', fileTypeId);
    files.forEach((file) => formData.append(file.name, file, file.name));

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    await this.apiService.post(`/cascade/${entityId}/files`, formData, config);
  }

  async getPromises(entityId, abortSignal) {
    const result = await this.apiService.get(
      `/cascade/${entityId}/promises`,
      undefined,
      { signal: abortSignal },
    );

    return result.data.data.items;
  }

  async getPledges(entityId, abortSignal) {
    const result = await this.apiService.get(
      `/cascade/${entityId}`,
      undefined,
      { signal: abortSignal },
    );

    return result.data.data.items[0];
  }

  async getComments(entityId) {
    const result = await this.apiService.get(`/cascade/${entityId}/comments`);

    return result.data.data.items;
  }

  async getDebtorInfo(entityId) {
    const result = await this.apiService.get(`/cascade/${entityId}/debtor`);

    return result.data.data.items[0];
  }

  async createComment(entityId, comment) {
    const result = await this.apiService.post(`/cascade/${entityId}/comments`, {
      content: comment,
    });

    return result.data.data.items;
  }

  mapValuesToPayload = (data) => {
    const { assignees, customer, construction } = data;
    const assigneeIds = map(assignees, 'id');
    const customerId = get(customer, 'id');
    const constructionId = get(construction, 'id');
    const due = convertDateToISOFormat(data.due);

    return {
      ...data,
      due,
      assignees: assigneeIds,
      customer: customerId,
      construction: constructionId,
    };
  };

  async createTask(entityId, data) {
    const payload = this.mapValuesToPayload(data);
    const result = await this.apiService.post(
      `/cascade/${entityId}/tasks`,
      payload,
    );
    return result.data.data.data;
  }

  async getTasks(entityId, abortSignal) {
    const result = await this.apiService.get(
      `/cascade/${entityId}/tasks`,
      undefined,
      { signal: abortSignal },
    );

    return result.data.data.items;
  }

  async getContacts(entityId) {
    const result = await this.apiService.get(`/cascade/${entityId}/contacts`);

    return result.data.data.items;
  }

  async getContracts(entityId) {
    const result = await this.apiService.get(`/cascade/${entityId}/contracts`);

    return result.data.data.items;
  }

  async getManagerInfo(entityId) {
    const result = await this.apiService.get(
      `/cascade/${entityId}/manager_info`,
    );

    return result.data.data.items[0];
  }

  async createPromise(entityId, data) {
    const { due, name, contract, ...other } = data;
    const newDue = convertDateFormat(due, sapFullDateFormat);
    const newName = get(name, 'name');
    const newContract = get(contract, 'contract');

    const newData = {
      due: newDue,
      name: newName,
      contract: newContract,
      ...other,
    };

    const result = await this.apiService.post(
      `/cascade/${entityId}/promises`,
      newData,
    );

    return result.data.data.items;
  }

  async getCompanyCodes(entityId) {
    const result = await this.apiService.get(
      `/cascade/${entityId}/company_codes`,
    );

    return result.data.data.items;
  }

  async getFileTypes() {
    const result = await this.apiService.get('/cascade/file_types');

    return result.data.data.items;
  }

  async createRevisionAct(entityId, data) {
    const { companyCode, period, email, ...other } = data;
    const [dateStart, dateEnd] = period;
    const newDateStart = formatDate(dateStart, sapFullDateFormat);
    const newDateEnd = formatDate(dateEnd, sapFullDateFormat);
    const newCompanyCode = get(companyCode, 'id');
    const newEmail = get(email, 'email');
    const hasEmail = data.email !== undefined;
    const responseType = hasEmail ? 'json' : 'blob';

    const newData = {
      email: newEmail,
      period: {
        dateStart: newDateStart,
        dateEnd: newDateEnd,
      },
      companyCode: newCompanyCode,
      ...other,
    };

    const result = await this.apiService.post(
      `/cascade/${entityId}/revision_act`,
      newData,
      {
        responseType,
      },
    );

    if (!hasEmail) {
      const header = result.headers['content-disposition'];
      const filename = extractFilenameFromHeader(header);

      downloadFile(result.data, filename);
    }

    return hasEmail;
  }

  async createClaim(entityId, data) {
    const { claimDate, openDebt, contract, email, ...other } = data;
    const newClaimDate = formatDate(claimDate, sapFullDateFormat);
    const newEmail = get(email, 'email');
    const contractId = get(contract, 'contract');
    const hasEmail = data.email !== undefined;
    const responseType = hasEmail ? 'json' : 'blob';

    const newData = {
      claimDate: newClaimDate,
      contract: contractId,
      email: newEmail,
      ...other,
    };

    const result = await this.apiService.post(
      `/cascade/${entityId}/claim`,
      newData,
      {
        responseType,
      },
    );

    if (!hasEmail) {
      const header = result.headers['content-disposition'];
      const filename = extractFilenameFromHeader(header);

      downloadFile(result.data, filename);
    }

    return hasEmail;
  }

  async createInvoice(entityId, data) {
    const { email, ...other } = data;
    const newEmail = get(email, 'email');
    const hasEmail = data.email !== undefined;
    const responseType = hasEmail ? 'json' : 'blob';

    const newData = {
      email: newEmail,
      ...other,
    };

    const result = await this.apiService.post(
      `/cascade/${entityId}/payment_calendar`,
      newData,
      {
        responseType,
      },
    );

    if (!hasEmail) {
      const header = result.headers['content-disposition'];
      const filename = extractFilenameFromHeader(header);

      downloadFile(result.data, filename);
    }

    return hasEmail;
  }
}

decorate(inject(SERVICE_IDENTIFIER.ApiService), CascadeService, 0);

export default CascadeService;
